import React, { useCallback, useEffect, useState, useMemo } from "react"
import { Row, Col, Card, CardBody, CardTitle, Button } from "reactstrap"

// datatable related plugins
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import "./datatables.scss"

const DatatableTables = (props) => {

  const { rowEvents } = props;

  const initialState = {
    page: 1,
    sizePerPage: 20,
    data: props.data,
    columns: props.columns,
  }
  const [state, setState] = useState(initialState);

  const defaultSorted = [];

  const afterDataChange = useCallback(() => {
    setState({
      page: 1,
      sizePerPage: 20,
      data: props.data,
      columns: props.columns
    })
  }, [props.data, props.columns, setState])

  useEffect(() => {
    afterDataChange()
  }, [afterDataChange, props.data])

  // Transform columns to new format
  const transformedColumns = useMemo(() => {
    return state.columns.map(col => ({
      accessorKey: col.dataField || col.accessorKey,
      header: col.text || col.header,
      cell: col.formatter ? (info) => col.formatter(info.getValue()) : undefined,
      sortValue: col.sortValue,
      searchValue: col.searchValue,
      sortable: col.sort !== false,
      searchable: col.searchable !== false,
    }))
  }, [state.columns])

  const table = useReactTable({
    data: state.data,
    columns: transformedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: state.sizePerPage,
      },
    },
  })

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <Card>
            <CardBody>
              <CardTitle className="h4">{props.title}</CardTitle>
              <p className="card-title-desc">
                {props.desc}
              </p>
              
              <div className="table-responsive">
                <table className="table align-middle table-nowrap">
                  <thead className="thead-light">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th 
                            key={header.id}
                            onClick={header.column.getToggleSortingHandler()}
                            style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted()] ?? null}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr 
                        key={row.id}
                        onClick={(e) => {
                          if (rowEvents && rowEvents.onClick) {
                            rowEvents.onClick(e, row.original, row.index)
                          }
                        }}
                        style={{ cursor: rowEvents && rowEvents.onClick ? 'pointer' : 'default' }}
                      >
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell || cell.getValue(),
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex align-items-center justify-content-between mt-3">
                <div>
                  <span className="text-muted">
                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                    {Math.min(
                      (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                      table.getFilteredRowModel().rows.length
                    )}{' '}
                    of {table.getFilteredRowModel().rows.length} results
                  </span>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    size="sm"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )

}

export default DatatableTables